/**
 * 3D Room Visualizer — Scene Builder
 *
 * Sets up the Three.js scene with corrected lighting for accurate color
 * rendering. White surfaces will appear white, not gray.
 */

import * as THREE from 'three';
import { LightProbeGenerator } from 'three/examples/jsm/lights/LightProbeGenerator.js';

/**
 * Configure renderer for color-accurate output.
 * CRITICAL: NoToneMapping prevents ACES filmic from darkening whites.
 */
export function configureRenderer(renderer: THREE.WebGLRenderer, enableShadows = false): void {
  renderer.toneMapping = THREE.NoToneMapping;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  // Supersample at 2x minimum for crisp lines and reduced flicker
  renderer.setPixelRatio(Math.max(window.devicePixelRatio, 2));

  if (enableShadows) {
    renderer.shadowMap.enabled = true;
    // VSMShadowMap supports shadow.radius for adjustable soft shadows
    renderer.shadowMap.type = THREE.VSMShadowMap;
  }
}

/**
 * Create and configure the scene with proper lighting.
 * Uses hemisphere light for natural fill + directional for depth.
 */
export function createScene(): THREE.Scene {
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0xf9fafb);
  return scene;
}

/** The lights addLighting puts in a scene, for callers that adjust them afterwards. */
export interface SceneLights {
  hemi: THREE.HemisphereLight;
  key: THREE.DirectionalLight;
  fill: THREE.DirectionalLight;
}

/**
 * Add lighting to the scene.
 * Hemisphere light provides natural sky/ground fill.
 * Single directional light adds subtle shadows for depth.
 */
export function addLighting(scene: THREE.Scene, flipY = false): SceneLights {
  // flipY: for ceiling-only mode, all lights come from below (negative Y)
  const ySign = flipY ? -1 : 1;

  // Hemisphere light: sky color (white) and ground bounce (slightly warm)
  const hemiLight = new THREE.HemisphereLight(0xffffff, 0xf0f0f0, 0.05);
  hemiLight.position.set(0, 20 * ySign, 0);
  scene.add(hemiLight);

  // Directional light for subtle shading and depth cues
  const dirLight = new THREE.DirectionalLight(0xffffff, 0.3);
  dirLight.position.set(5, 10 * ySign, 7);
  scene.add(dirLight);

  // Secondary fill light from opposite side to reduce dark faces
  const fillLight = new THREE.DirectionalLight(0xffffff, 0.15);
  fillLight.position.set(-5, 8 * ySign, -7);
  scene.add(fillLight);

  return { hemi: hemiLight, key: dirLight, fill: fillLight };
}

/**
 * Add an interior point light inside the room for natural shadow casting.
 * Positioned at ceiling height, centered in the room.
 * Returns the light so the caller can reposition if needed.
 */
/**
 * Add an interior point light inside the room for natural shadow casting.
 * IMPORTANT: Must be added to the roomGroup (not the scene) because
 * roomGroup is offset so coordinates are in room-local space.
 *
 * @param parent - The roomGroup to add the light to
 */
export function addInteriorLight(
  parent: THREE.Object3D,
  roomLength: number,
  roomWidth: number,
  roomHeight: number
): THREE.PointLight {
  const yPos = roomHeight * 0.75;
  const light = new THREE.PointLight(0xfff5e6, 4.85, 13, 0.25);
  light.position.set(roomLength / 2, yPos, roomWidth / 2);

  light.castShadow = true;
  light.shadow.mapSize.width = 1024;
  light.shadow.mapSize.height = 1024;
  light.shadow.camera.near = 0.5;
  light.shadow.camera.far = Math.max(roomLength, roomWidth, roomHeight) * 3;
  light.shadow.bias = -0.00274;
  light.shadow.normalBias = 0.085;
  light.shadow.radius = 2.5;

  parent.add(light);
  return light;
}

/**
 * Add interior lighting + envMap probe to any viewer type.
 * Handles room, wall, and ceiling viewers with appropriate light positioning.
 *
 * @param scene - The Three.js scene
 * @param parent - Group to add the point light to (roomGroup or wall group)
 * @param renderer - WebGL renderer (needs shadows enabled)
 * @param mode - 'room' | 'wall' | 'ceiling'
 * @param dims - { length, width, height } of the surface being viewed
 * @param onReady - Called when envMap + probe are loaded
 */
export function addViewerLighting(
  scene: THREE.Scene,
  parent: THREE.Object3D,
  renderer: THREE.WebGLRenderer,
  mode: 'room' | 'wall' | 'ceiling',
  dims: { length: number; width: number; height: number },
  onReady?: (light: THREE.PointLight, setup: LightProbeSetup) => void
): THREE.PointLight {
  let lightX: number, lightY: number, lightZ: number;
  let intensity: number, decay: number, distance: number;
  let shadowMapSize: number, shadowBias: number, shadowNormalBias: number, shadowRadius: number;

  if (mode === 'room') {
    lightX = dims.length / 2;
    lightY = dims.height * 0.75;
    lightZ = dims.width / 2;
    intensity = 4.85; decay = 0.25; distance = 13;
    shadowMapSize = 1024; shadowBias = -0.00274; shadowNormalBias = 0.085; shadowRadius = 2.5;
  } else if (mode === 'wall') {
    lightX = dims.length / 2;
    lightY = dims.height * 0.80;
    lightZ = dims.length / 2;
    intensity = 4; decay = 0.25; distance = 15.5;
    shadowMapSize = 1024; shadowBias = -0.0019; shadowNormalBias = 0.11; shadowRadius = 6.5;
  } else {
    lightX = dims.length / 2;
    lightY = -dims.height * 0.70;
    lightZ = dims.width / 2;
    intensity = 4.85; decay = 0.25; distance = 13;
    shadowMapSize = 256; shadowBias = -0.00362; shadowNormalBias = 0.085; shadowRadius = 2.5;
  }

  const light = new THREE.PointLight(0xfff5e6, intensity, distance, decay);
  light.position.set(lightX, lightY, lightZ);

  light.castShadow = true;
  light.shadow.mapSize.width = shadowMapSize;
  light.shadow.mapSize.height = shadowMapSize;
  light.shadow.camera.near = 0.5;
  light.shadow.camera.far = Math.max(dims.length, dims.width, dims.height) * 3;
  light.shadow.bias = shadowBias;
  light.shadow.normalBias = shadowNormalBias;
  light.shadow.radius = shadowRadius;

  parent.add(light);

  // Load envMap + probe
  addLightProbe(scene, renderer, ENV_IMAGES[2].path, (setup) => {
    setup.probe.intensity = 0.4;
    scene.environment = setup.envMap;

    // Set envMapIntensity (and wall-mode roughness override) on all materials
    parent.traverse(child => {
      if (child instanceof THREE.Mesh && child.material instanceof THREE.MeshStandardMaterial) {
        child.material.envMapIntensity = 0.5;
        if (mode === 'wall') {
          child.material.roughness = 0.5;
        } else if (mode === 'ceiling') {
          child.material.roughness = 0.5;
          child.material.metalness = 0;
          child.material.emissiveIntensity = 0.28;
        }
      }
    });

    if (onReady) onReady(light, setup);
  });

  return light;
}

/**
 * Result of setting up a light probe with environment map.
 */
export interface LightProbeSetup {
  probe: THREE.LightProbe;
  envMap: THREE.Texture | null;
}

/** Available environment image paths */
export const ENV_IMAGES = [
  { label: 'Garage 1 (Trusscore + doors)', path: '/textures/garage-env-1.jpg' },
  { label: 'Garage 2 (Slatwall workshop)', path: '/textures/garage-env-2.jpg' },
  { label: 'Garage 3 (Open door daylight)', path: '/textures/garage-env-3.jpg' },
];

// ---------------------------------------------------------------------------
// Env-map cache: spherical-harmonics coefficients only
// ---------------------------------------------------------------------------
// Cross-renderer texture sharing isn't possible (WebGL contexts are isolated),
// so per-renderer GPU work (PMREM cubemap) stays per-renderer. But the probe's
// spherical-harmonics coefficients ARE plain Vector3 floats and renderer-
// independent — once any renderer has computed them for a given URL, every
// subsequent renderer can reuse them and skip the LightProbeGenerator step
// (which is the heaviest CPU step in probe setup).
//
// Image loading itself stays on THREE.TextureLoader, which has its own
// internal cache (THREE.Cache) and dedupes requests for the same URL.

const shCoefficientsCache = new Map<string, THREE.Vector3[]>();

function applyCachedShOrCompute(
  probe: THREE.LightProbe,
  imagePath: string,
  renderer: THREE.WebGLRenderer,
  renderTarget: THREE.WebGLRenderTarget,
): void {
  const cached = shCoefficientsCache.get(imagePath);
  if (cached) {
    for (let i = 0; i < 9; i++) {
      probe.sh.coefficients[i].copy(cached[i]);
    }
    return;
  }
  const generated = LightProbeGenerator.fromCubeRenderTarget(
    renderer,
    renderTarget as unknown as THREE.WebGLCubeRenderTarget,
  );
  for (let i = 0; i < 9; i++) {
    probe.sh.coefficients[i].copy(generated.sh.coefficients[i]);
  }
  shCoefficientsCache.set(
    imagePath,
    generated.sh.coefficients.map((v) => v.clone()),
  );
}

/**
 * Add a LightProbe to the scene using a JPG image.
 * The image is loaded, converted via PMREMGenerator to create an
 * environment map and spherical harmonics for the probe.
 *
 * @param scene - The scene to add the probe to
 * @param renderer - Renderer (needed for PMREM generation)
 * @param imagePath - Path to equirectangular-ish JPG image
 * @param onReady - Called when probe + envMap are ready
 */
export function addLightProbe(
  scene: THREE.Scene,
  renderer: THREE.WebGLRenderer,
  imagePath: string,
  onReady?: (setup: LightProbeSetup) => void
): THREE.LightProbe {
  const probe = new THREE.LightProbe();
  probe.intensity = 0.4;
  scene.add(probe);

  const loader = new THREE.TextureLoader();
  loader.load(imagePath, (texture) => {
    texture.mapping = THREE.EquirectangularReflectionMapping;
    texture.colorSpace = THREE.SRGBColorSpace;

    const pmremGenerator = new THREE.PMREMGenerator(renderer);
    pmremGenerator.compileEquirectangularShader();
    const renderTarget = pmremGenerator.fromEquirectangular(texture);
    const envMap = renderTarget.texture;

    const savedIntensity = probe.intensity;
    applyCachedShOrCompute(probe, imagePath, renderer, renderTarget);
    probe.intensity = savedIntensity;

    texture.dispose();
    pmremGenerator.dispose();

    if (onReady) {
      onReady({ probe, envMap });
    }
  });

  return probe;
}

/**
 * Load a new environment image and update the probe + envMap.
 * Used by the lighting panel to switch between images at runtime.
 */
export function loadEnvImage(
  renderer: THREE.WebGLRenderer,
  imagePath: string,
  onReady: (envMap: THREE.Texture, probe: THREE.LightProbe) => void
): void {
  const loader = new THREE.TextureLoader();
  loader.load(imagePath, (texture) => {
    texture.mapping = THREE.EquirectangularReflectionMapping;
    texture.colorSpace = THREE.SRGBColorSpace;

    const pmremGenerator = new THREE.PMREMGenerator(renderer);
    pmremGenerator.compileEquirectangularShader();
    const renderTarget = pmremGenerator.fromEquirectangular(texture);
    const envMap = renderTarget.texture;

    const probe = new THREE.LightProbe();
    applyCachedShOrCompute(probe, imagePath, renderer, renderTarget);

    texture.dispose();
    pmremGenerator.dispose();

    onReady(envMap, probe);
  });
}

/**
 * Create a perspective camera sized to fit the room.
 */
export function createCamera(
  containerWidth: number,
  containerHeight: number,
  roomLength: number,
  roomWidth: number,
  roomHeight: number
): { camera: THREE.PerspectiveCamera; distance: number } {
  const aspect = containerWidth / containerHeight;
  const camera = new THREE.PerspectiveCamera(50, aspect, 0.1, 1000);

  // Calculate distance to fit the room with comfortable margins
  // The room is viewed at an isometric-like angle, so diagonal extent matters
  const maxDim = Math.max(roomLength, roomWidth, roomHeight);
  const fov = camera.fov * (Math.PI / 180);
  const distance = (maxDim / 2) / Math.tan(fov / 2) * 1.5;

  return { camera, distance };
}

/**
 * Add edge borders to a mesh (dark outline around surfaces).
 */
export function addBorders(geometry: THREE.BufferGeometry, mesh: THREE.Mesh): void {
  const edges = new THREE.EdgesGeometry(geometry);
  const lineMaterial = new THREE.LineBasicMaterial({
    color: 0x333333,
    linewidth: 1,
    transparent: true,
    opacity: 0.6,
  });
  const border = new THREE.LineSegments(edges, lineMaterial);
  mesh.add(border);
}
